import React, { useState } from "react";

/**
 * Welcome / onboarding video.
 *
 * Priority: self-hosted `videoUrl` → YouTube `youtubeId` → branded placeholder
 * with the spoken script. Autoplay is muted (browser policy); a single click
 * unmutes. The overlay never blocks the rest of the flow.
 */
const youtubeEmbedSrc = (youtubeId, { autoPlay, soundOn }) => {
  const params = new URLSearchParams({
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
    autoplay: autoPlay ? "1" : "0",
    mute: soundOn ? "0" : "1",
  });
  return `https://www.youtube-nocookie.com/embed/${youtubeId}?${params.toString()}`;
};

const OnboardingVideo = ({ video, onStarted, onCompleted, autoPlay = true }) => {
  const { videoUrl, youtubeId, posterUrl, captionsUrl, script, durationHint } = video || {};
  const [failed, setFailed] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const reduceMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const shouldAutoPlay = autoPlay && !reduceMotion;

  const showPlaceholder = (!videoUrl && !youtubeId) || failed;

  if (showPlaceholder) {
    return (
      <div className="ob-video ob-video--placeholder" role="img" aria-label="Founder welcome message">
        <span className="ob-video__badge">
          <span className="ob-video__play">▶</span>
          Founder message{durationHint ? ` · ${durationHint}s` : ""}
        </span>
        {script && <p className="ob-video__script">{script}</p>}
      </div>
    );
  }

  if (youtubeId) {
    return (
      <div className="ob-video ob-video--youtube">
        <iframe
          key={soundOn ? "sound" : "muted"}
          className="ob-video__el"
          src={youtubeEmbedSrc(youtubeId, { autoPlay: shouldAutoPlay || soundOn, soundOn })}
          title="Emrah, founder of RouteBot"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onLoad={() => {
            if (onStarted) onStarted();
          }}
        />
        {!soundOn && (
          <button
            type="button"
            className="ob-video__unmute"
            onClick={() => {
              setSoundOn(true);
              if (onStarted) onStarted();
            }}
          >
            Unmute
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="ob-video">
      <video
        className="ob-video__el"
        src={videoUrl}
        poster={posterUrl || undefined}
        muted={!soundOn}
        playsInline
        controls
        autoPlay={shouldAutoPlay}
        preload="metadata"
        onPlay={onStarted}
        onEnded={onCompleted}
        onError={() => setFailed(true)}
      >
        {captionsUrl && <track kind="captions" src={captionsUrl} srcLang="en" label="English" default />}
      </video>
    </div>
  );
};

export default OnboardingVideo;
